"use client";

import { ArrowRight, Play } from "lucide-react";
import type { PaginatedActivityGridItem } from "../../types";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";
import { resolveImageValue } from "../../utils/imageOptions";
import { StatusBadge } from "./StatusBadge";

/**
 * Document-style card : used by the generic paginated index pages (communiqués,
 * publications, …) AND by the home "latest videos" strip. Rendered inside a
 * responsive grid. Each card is a clean, equal-height card: a small type/meta
 * line, the title as a prominent brand-blue link, an optional two-line
 * description and a "Consulter"/"Regarder" affordance pinned to the bottom.
 *
 * Optional thumbnail (e.g. video miniatures on /actualites/videos and the home
 * video strip). Document lists without an image field (communiqués,
 * publications) fall through to the imageless card : the image strip is only
 * rendered when a real key is resolved, so nothing changes for them. When a
 * thumbnail IS present a centered play badge reads the tile as a video and the
 * bottom affordance switches from "Consulter" to "Regarder".
 *
 * On the site palette, with the white-card / slate-200 border / soft blue
 * shadow convention shared with `InformationHub` and `Gallery`.
 */
export function DocumentCard({
  item,
  titleNode,
  showMeta = true,
}: {
  item: PaginatedActivityGridItem;
  /** Pre-rendered title (e.g. search-highlighted <mark> nodes) shown INSTEAD
   *  of `item.title`. The plain string keeps feeding alt text. */
  titleNode?: import("react").ReactNode;
  /** `false` drops the meta line (the record date) and keeps the document icon
   *  alone, exactly as on a record that carries no date. Driven by the grid's
   *  `showDate`: the publication listings state the year in the title and the bank
   *  does not want a second, publication-date reading next to it. */
  showMeta?: boolean;
}) {
  const locale = useLocale();
  const href = resolveLinkHref(item.href);
  const meta = showMeta ? (item.meta ?? "").toString().trim() : "";
  const description = (item.description ?? "").toString().trim();
  // Quand la ligne du haut est libre (grille sans date) et que la fiche porte un
  // libellé de type, celui-ci y monte : il occupe la place que tenait la date, au
  // lieu de flotter sous le titre. Les notes de conjoncture en sont le cas réel,
  // avec leur périodicité. Une grille qui affiche sa date garde sa mise en page.
  const metaLine = meta || description;
  const bodyDescription = meta ? description : "";
  const { src: imageSrc } = resolveImageValue(item.imageSrc);
  const hasImage = Boolean(imageSrc);
  // Valeur de la fiche, déjà mise en forme par le projecteur. Émise uniquement par
  // les types à valeur UNIQUE ; ailleurs elle est absente et la carte ne bouge pas.
  const valeur = (item.value ?? "").toString().trim();
  // Fiches à PLUSIEURS chiffres (les encours des Bons BCC et leurs cinq maturités).
  // Les aligner sur une seule ligne les faisait tronquer et masquait l'unité : ils
  // se rangent donc sous le titre, une par ligne, libellé à gauche et montant à
  // droite, comme un relevé.
  const valeurs = Array.isArray(item.values) ? item.values.filter((v) => v && v.value) : [];

  // Plus d'icone de document sur la carte : retiree a la demande de la Banque le
  // 12/08/2026. La carte s'ouvre donc sur sa ligne meta, ou directement sur son
  // titre quand il n'y a rien a mettre au-dessus.
  const titleClassName =
    "text-[16px] xl:text-[18px] font-semibold leading-snug text-[#005CA9] group-hover:underline";

  const inner = (
    <article className="flex h-full flex-col overflow-hidden rounded-lg border border-bcc-panel bg-white shadow-bcc-panel transition-all duration-200 group-hover:-translate-y-0.5 group-hover:border-[#005CA9]/40">
      {hasImage ? (
        <div className="relative aspect-video w-full overflow-hidden bg-slate-100">
          <RuntimeImage
            src={imageSrc as string}
            alt={item.imageAlt || item.title || ""}
            className="absolute inset-0 h-full w-full object-cover transition duration-200 group-hover:scale-[1.02]"
          />
          <span
            className="absolute inset-0 grid place-items-center"
            aria-hidden="true"
          >
            <span className="grid h-12 w-12 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm transition group-hover:bg-[#005CA9]">
              <Play className="h-5 w-5 translate-x-[1px] fill-current" />
            </span>
          </span>
        </div>
      ) : null}
      <div className={`flex flex-1 flex-col p-4 sm:p-5 ${hasImage ? "pt-4 sm:pt-4" : ""}`}>
      {/* Ligne du haut remplie (date, ou libellé de type remonté) : le titre passe
          dessous. Ligne vide : le titre commence la carte, sans blanc au-dessus. La
          pastille de statut juridique, quand la fiche en porte une, occupe la droite
          de cette même ligne. */}
      {item.badge?.label ? (
        <div className="flex items-center justify-between gap-2">
          {metaLine ? (
            <div className="truncate text-[12px] xl:text-[13px] font-medium uppercase tracking-wide text-[#6B7A99]">
              {metaLine}
            </div>
          ) : (
            <span aria-hidden="true" />
          )}
          <StatusBadge badge={item.badge} />
        </div>
      ) : metaLine ? (
        <div className="truncate text-[12px] xl:text-[13px] font-medium uppercase tracking-wide text-[#6B7A99]">
          {metaLine}
        </div>
      ) : null}
      <h3 className={`${metaLine || item.badge?.label ? "mt-3" : ""} ${titleClassName}`}>
        {titleNode ?? item.title}
      </h3>
      {bodyDescription ? (
        <p className="mt-2 line-clamp-2 text-[14px] xl:text-[15px] leading-snug text-[#0F2849]">
          {bodyDescription}
        </p>
      ) : null}
      {valeurs.length ? (
        <dl className="mt-3 space-y-1 border-t border-bcc-panel pt-3">
          {valeurs.map((v, i) => (
            <div key={`${v.label}-${i}`} className="flex items-baseline justify-between gap-3">
              <dt className="shrink-0 text-[12px] xl:text-[13px] font-medium text-[#6B7A99]">{v.label}</dt>
              <dd className="text-right text-[13px] xl:text-[14px] font-medium tabular-nums text-[#5D6E8C]">
                {v.value}
                {v.unit ? <span className="ml-1 font-medium text-[#6B7A99]">{v.unit}</span> : null}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {/* Pied de carte : l'appel à l'action à gauche, le chiffre de la fiche à sa
          droite, sur la même ligne de base. Sur une série (un taux, un encours), ce
          chiffre EST l'information : le laisser derrière un clic obligeait à ouvrir
          chaque fiche pour le lire. Flotter au milieu de la carte lui donnait un
          alignement qui ne répondait à rien.

          `flex-wrap-reverse` tient la promesse sur écran étroit : quand
          « 2 693 580 785 499,10 CDF » ne tient plus à côté du lien, il passe AU-DESSUS
          et non en dessous, l'appel à l'action restant la dernière chose lue.

          Gris ardoise et non bleu nuit : en gras et sur douze chiffres, le ton du
          corps de texte tirait l'oeil avant le titre. */}
      <div className="mt-auto flex flex-wrap-reverse items-baseline justify-between gap-x-3 gap-y-1 pt-4">
        <span className="inline-flex items-center gap-1.5 text-[13px] xl:text-[14px] font-semibold text-[#005CA9]">
          {hasImage ? t(locale, "watch") : t(locale, "view")}
          <ArrowRight className="h-4 w-4 text-[#FFA401] transition group-hover:translate-x-0.5" />
        </span>
        {valeur ? (
          <p className="ml-auto text-right text-[18px] xl:text-[20px] font-bold tabular-nums leading-tight text-[#5D6E8C]">
            {valeur}
            {item.valueUnit ? (
              <span className="ml-1 text-[13px] xl:text-[14px] font-semibold text-[#6B7A99]">{item.valueUnit}</span>
            ) : null}
          </p>
        ) : null}
      </div>
      </div>
    </article>
  );

  return href ? (
    <RuntimeLink href={href} className="group block h-full">
      {inner}
    </RuntimeLink>
  ) : (
    <div className="group h-full">{inner}</div>
  );
}
